import { differenceInMilliseconds } from 'date-fns';
import { CaseResourceType } from '@/api/apis/case';
import { AircraftUtilization, AviaLegTypes, GroundLegTypes, RouteLegTypeEnum } from '@/api/apis/route';

const FALLBACK_CASE_LABEL = 'another case';

export type AircraftTimeRange = {
  from: string;
  to: string;
};

export const getLegAircraftId = (leg: AviaLegTypes | GroundLegTypes) =>
  leg.resources.find((r) => r.resourceType === CaseResourceType.Aircraft)?.resourceId;

export const getMainRouteAircraftRanges = (legs: (AviaLegTypes | GroundLegTypes)[]) => {
  return legs.reduce<Map<string, AircraftTimeRange>>((ranges, leg) => {
    if (leg.legType !== RouteLegTypeEnum.TmdxJetAviaLeg || !leg.departureTime || !leg.arrivalTime) {
      return ranges;
    }

    const aircraftId = getLegAircraftId(leg);

    if (!aircraftId) {
      return ranges;
    }

    const range = ranges.get(aircraftId);

    if (!range) {
      ranges.set(aircraftId, { from: leg.departureTime, to: leg.arrivalTime });
      return ranges;
    }

    ranges.set(aircraftId, {
      from: differenceInMilliseconds(leg.departureTime, range.from) < 0 ? leg.departureTime : range.from,
      to: differenceInMilliseconds(leg.arrivalTime, range.to) > 0 ? leg.arrivalTime : range.to,
    });

    return ranges;
  }, new Map());
};

export const getAircraftIdsQueryValue = (ranges: Map<string, AircraftTimeRange>) => Array.from(ranges.keys()).join(',');

export const getUnionRange = (ranges: Map<string, AircraftTimeRange>): AircraftTimeRange | null => {
  return Array.from(ranges.values()).reduce<AircraftTimeRange | null>((union, range) => {
    if (!union) return { ...range };

    return {
      from: differenceInMilliseconds(range.from, union.from) < 0 ? range.from : union.from,
      to: differenceInMilliseconds(range.to, union.to) > 0 ? range.to : union.to,
    };
  }, null);
};

export const isUtilizationInRange = (
  utilization: Pick<AircraftUtilization, 'departureTime' | 'arrivalTime'>,
  range: AircraftTimeRange,
) =>
  differenceInMilliseconds(utilization.arrivalTime, range.from) >= 0 &&
  differenceInMilliseconds(utilization.departureTime, range.to) <= 0;

export const getAircraftUtilizationWarningPrefix = (tailNumber?: string | null) =>
  ['The aircraft', tailNumber, 'is engaged on overlapping cases.'].filter(Boolean).join(' ');

export const getAircraftUtilizationWarningText = (utilization: AircraftUtilization, tailNumber?: string | null) => {
  const prefix = getAircraftUtilizationWarningPrefix(tailNumber || utilization.aircraftResource.registration);
  const caseLabel = utilization.caseName || FALLBACK_CASE_LABEL;

  return `${prefix} ${caseLabel}`;
};
